import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import {
  andWhere,
  buildQuery,
  CombinedFilter,
  FilterFn,
  noop,
  Ops,
  orderBy,
  OrderVariant,
  orWhere,
  page,
  select,
  where,

} from "@albatrosdeveloper/ave-utils-npm/lib/utils/query.util";
import { HttpClient } from "@angular/common/http";
import {
  IPagination,
  IPaginationResult,
  IRequestResult,
} from "../../../../common/interfaces";
import { environment as env } from "../../../../../environments/environment.prod";
import { StatusEnum } from "@albatrosdeveloper/ave-models-package/lib/enums";
import { AbstractService } from "../../../../services/abstract.service";
import PriceListAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/masters/price-list/price-list.entity";
import { ICreatePriceList } from "./interfaces/create-price-list.interface";
import { IUpdatePriceList } from "./interfaces/update-price-list.interface";
import { RequestOptions } from "app/common/interfaces/options.interface";
import { PartnerChainIds, UpdatePartnersResponse } from "./modals/dto/PartnerChainIds.interface";
const apiUrl = env.API_UNIT_MEASURE;
const apiUrlBusinessPartner = env.API_BUSINESS_PARTNER;
const moduleName = "price_list";

@Injectable({
  providedIn: "root",
})
export class PriceListService extends AbstractService<
  PriceListAttributes,
  ICreatePriceList,
  IUpdatePriceList
> {


  /** The last filter used to load the list */
  currentFilter: CombinedFilter<PriceListAttributes>;

  constructor(
    http: HttpClient,
  ) {
    super(apiUrl, moduleName, http);
  }

  postBusinessToPriceList(priceListId:string, data: PartnerChainIds) {
      const API_URL: string = `${apiUrlBusinessPartner}api/business_partner_distribution_chain/bp/price_list/${priceListId}`;
      return this.http.patch<IRequestResult<UpdatePartnersResponse>>(API_URL, data);
    }

  /**
   * Obtiene listas de precios con paginación y filtros.
   *
   * Estrategia de búsqueda:
   * - Sin búsqueda: Un solo objeto con filtros base
   * - Con búsqueda: Dos objetos (OR lógico) - busca en 'code', 'name'
   *
   * Cada objeto del OR debe incluir TODAS las condiciones base para mantener consistencia.
   *
   * @param options Pagination options
   * @param filter Filters to apply
   * @param status Status filter
   * @returns Paginated data
   *
   * Search fields: code, name
   * Array filters: type, status
   */
  findAllWithCount(
    options: IPagination<PriceListAttributes>,
    filter?: any,
    status?
  ): Observable<IPaginationResult<PriceListAttributes>> {

    const hasSearch = !!options.search;

    /**
     * Construye las condiciones base que SIEMPRE deben aplicarse.
     * Incluye: _deleted=false, filtro de tipo, estados.
     *
     * @param useOrWhere - Si es true, inicia un nuevo objeto con orWhere (para el OR lógico)
     */
    const buildBaseFilters = (params: { useOrWhere: boolean }): FilterFn<PriceListAttributes>[] => {
      const deletedFilter: FilterFn<PriceListAttributes> = params.useOrWhere
        ? orWhere('_deleted', Ops.eq(false))  // Inicia nuevo objeto en el array
        : where('_deleted', Ops.eq(false));   // Primer objeto del array

      return [
        deletedFilter,
        filter?.type && filter.type.length > 0
          ? andWhere('type', Ops.in(...filter.type))
          : noop(),
        status && status.length > 0
          ? andWhere('statusId', Ops.in(...status))
          : noop()
      ];
    };

    // ========== CONSTRUCCIÓN DE OBJETOS JSON ==========

    // Objeto 1: Condiciones base + búsqueda por CODE (o solo condiciones si no hay búsqueda)
    const searchByCodeQueryObject: FilterFn<PriceListAttributes>[] = [
      ...buildBaseFilters({ useOrWhere: false }),  // false = usa 'where' (primer objeto)
      hasSearch ? andWhere("code", Ops.like(options.search!, "i")) : noop()
    ];

    // Objeto 2: Condiciones base + búsqueda por NAME (solo si hay búsqueda)
    const searchByNameQueryObject: FilterFn<PriceListAttributes>[] = hasSearch ? [
      ...buildBaseFilters({ useOrWhere: true }),   // true = usa 'orWhere' (segundo objeto)
      andWhere("name", Ops.like(options.search!, "i"))
    ] : [];

    // ========== ENSAMBLAJE FINAL ==========

    const query = buildQuery<PriceListAttributes>(
      // Expandimos ambos objetos en el buildQuery
      ...searchByCodeQueryObject,
      ...searchByNameQueryObject,

      // Ordenamiento y paginación
      orderBy(
        options.sort ?? "name",
        options.sortDirection === "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    this.currentFilter = query;

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IPaginationResult<PriceListAttributes>>(API_URL);
  }

  /**
   * Find to combo box
   * @returns Data
   */
  findToComboBoxPriceList(type?): Observable<IRequestResult<PriceListAttributes[]>> {
    const query = buildQuery<PriceListAttributes>(
      where("statusId", StatusEnum.ACTIVE),
      type ? andWhere('type', Ops.eq(type)) : noop(),
      select(["_id", "code", "name", "type"]),
      orderBy("code", OrderVariant.Asc)
    );
    const API_URL: string =
      apiUrl +
      `api/${moduleName}/filter?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IRequestResult<PriceListAttributes[]>>(API_URL);
  }


  findToComboBoxDiscountList(type: string = 'D'): Observable<IRequestResult<PriceListAttributes[]>> {
    const query = buildQuery<PriceListAttributes>(
      where("statusId", StatusEnum.ACTIVE),
      andWhere('type', Ops.eq(type)),
      select(["_id", "code", "name"]),
      orderBy("code", OrderVariant.Asc)
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/filter?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IRequestResult<PriceListAttributes[]>>(API_URL);
  }

  findToComboBoxGiftList(type: string = 'G'): Observable<IRequestResult<PriceListAttributes[]>> {
    const query = buildQuery<PriceListAttributes>(
      where("statusId", StatusEnum.ACTIVE),
      andWhere('type', Ops.eq(type)),
      select(["_id", "code", "name"]),
      orderBy("code", OrderVariant.Asc)
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/filter?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IRequestResult<PriceListAttributes[]>>(API_URL);
  }


  findByFilter(filter: CombinedFilter<PriceListAttributes>, options?: RequestOptions) {
    const API_URL: string =
      apiUrl +
      `api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(filter))}`;
    const headers: Record<string, any> = {};
    // see order-customer-export for more info about this header
    // ctx: stands for context
    if (options?.contextHeader) headers['x-ave-ctx'] = options.contextHeader;
    return this.http.get<IPaginationResult<PriceListAttributes>>(API_URL, { headers });
  }


}
